import { bdTrackerAllowedRoles } from 'data/bdTracker/bdTrackerUserRoleValidations';
import { NADWaiverInfo, NADWaiverNoteRequest } from 'Interfaces/BDPortalInterfaces';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { TabPanel, TabView } from 'primereact/tabview';
import ReactQuill from 'react-quill';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import { DateConversionUtility } from 'utils/dateUtilities';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import NADWaiverLogic from './NADWaiver.logic';
import styles from './NADWaiver.module.scss';

const NADWaiverModal = ({
  visible,
  closeModal,
  opportunityIterationID,
  isIterationActive,
}: {
  visible: boolean;
  closeModal: () => void;
  opportunityIterationID: number;
  isIterationActive: boolean;
}) => {
  const {
    userModulesRoles,
    currentConceptsIndex,
    concepts,
    setCurrentConceptsIndex,
    NADWaiversByStrength,
    setNADWaiversByStrength,
    NADWaiverLoading,
    isError404: _isError404,
    savingStrength,
    saveStrength,
    hasUnsavedChanges: _hasUnsavedChanges,
    hasUnsavedChangesForStrength,
    handleClose: _handleClose,
    NADWaiverNoteLoading,
    note,
    setNote,
    handleNoteChange,
    saveNADWaiverNote,
    NADWaiverNotes,
    saveError,
    setSaveError,
    isConfirmModalOpen,
    handleCloseNADWaiverModal,
    handleConfirmForConfirmationModal,
    handleCloseConfirmationModal,
  } = NADWaiverLogic(opportunityIterationID, closeModal);

  const updateNADWaiver = (strength: string, updates: Partial<NADWaiverInfo>) => {
    if (
      'Status' in updates &&
      saveError?.filter(item => item.depends === 'Status') &&
      updates.Status?.toLowerCase() === 'pending'
    ) {
      setSaveError(null);
    } else if ('Submitted' in updates && saveError?.filter(item => item.depends === 'Submitted')) {
      setSaveError(null);
    }

    setNADWaiversByStrength(prev => {
      const currentWaiver = prev[strength];
      if (!currentWaiver) return prev;

      return {
        ...prev,
        [strength]: {
          ...currentWaiver,
          ...updates,
        },
      };
    });
  };

  return (
    <Dialog
      contentClassName={styles.modalContent}
      header={'NAD Waiver'}
      style={{ width: '40rem', height: '47rem', margin: 'auto' }}
      visible={visible}
      onHide={handleCloseNADWaiverModal}
      blockScroll
      draggable={false}
      resizable={false}
    >
      <>
        {NADWaiverLoading ? (
          <Skeleton height="5rem" width="100%"></Skeleton>
        ) : concepts && concepts?.Concept_info.length > 0 ? (
          <div style={{ width: '100%' }}>
            <TabView
              scrollable
              className={styles.customTab}
              activeIndex={currentConceptsIndex}
              onTabChange={e => {
                setCurrentConceptsIndex(e.index);
                setSaveError(null);
              }}
            >
              {concepts?.Concept_info.map((concept, index) => {
                const hasUnsaved = hasUnsavedChangesForStrength(concept.Strength);
                const currentNADWaiver = NADWaiversByStrength[concept.Strength];

                return (
                  <TabPanel
                    key={index}
                    header={
                      <span style={{ color: hasUnsaved ? 'red' : 'inherit' }}>
                        {concept.Strength}
                        {hasUnsaved && ' *'}
                      </span>
                    }
                    headerClassName={`${
                      currentConceptsIndex === index ? styles.selectedTab : styles.unselectedTab
                    } ${hasUnsaved ? styles.unsavedTab : ''}`}
                  >
                    <div className={styles.radioButtons} style={{ margin: '1rem 0' }}>
                      <div className="p-field p-col-6">
                        <input
                          type="radio"
                          name={`existing-${concept.Strength}`}
                          value="Yes"
                          checked={currentNADWaiver?.IsExisting === true}
                          onChange={() => updateNADWaiver(concept.Strength, { IsExisting: true })}
                          disabled={!isIterationActive}
                        />
                        <label htmlFor="existing-yes">Existing</label>
                      </div>
                      <div className="p-field p-col-6">
                        <input
                          type="radio"
                          name={`existing-${concept.Strength}`}
                          value="No"
                          checked={currentNADWaiver?.IsExisting === false}
                          onChange={() => updateNADWaiver(concept.Strength, { IsExisting: false })}
                          disabled={!isIterationActive}
                        />
                        <label htmlFor="existing-no">New</label>
                      </div>
                    </div>

                    <>
                      {currentNADWaiver?.IsExisting ? (
                        <div className={styles.dropdown} style={{ padding: '1rem' }}>
                          <label htmlFor="negotiationsDate">EXPIRATION DATE</label>
                          <Calendar
                            view="date"
                            dateFormat="mm/dd/yy"
                            value={DateConversionUtility.convertDBDateToLocal(
                              currentNADWaiver?.ExpirationDate
                            )}
                            onChange={e => {
                              const formattedDate =
                                DateConversionUtility.convertLocalToDBDateString(e.value as Date);
                              updateNADWaiver(concept.Strength, {
                                ExpirationDate: formattedDate as unknown as Date,
                              });
                            }}
                            showIcon
                            disabled={!isIterationActive}
                          />
                        </div>
                      ) : (
                        <div
                          className="p-grid"
                          style={{
                            padding: '1rem',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                              gap: '1rem',
                            }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <div className={styles.dropdown}>
                                <label htmlFor="negotiation">SUBMITTED</label>
                                <div className={styles.radioButtons} style={{ marginTop: '1rem' }}>
                                  <div className="p-field p-col-6">
                                    <input
                                      type="radio"
                                      name={`submitted-${concept.Strength}`}
                                      value="Yes"
                                      checked={currentNADWaiver?.Submitted === true}
                                      onChange={() =>
                                        updateNADWaiver(concept.Strength, { Submitted: true })
                                      }
                                      disabled={!isIterationActive}
                                    />
                                    <label htmlFor="negotiation-yes">Yes</label>
                                  </div>
                                  <div className="p-field p-col-6">
                                    <input
                                      type="radio"
                                      name={`submitted-${concept.Strength}`}
                                      value="No"
                                      checked={currentNADWaiver?.Submitted === false}
                                      onChange={() =>
                                        updateNADWaiver(concept.Strength, { Submitted: false })
                                      }
                                      disabled={!isIterationActive}
                                    />
                                    <label htmlFor="negotiation-no">No</label>
                                  </div>
                                </div>
                              </div>
                              <div className={styles.dropdown}>
                                <label htmlFor="negotiationsDate">
                                  SUBMISSION DATE{' '}
                                  {currentNADWaiver?.Submitted && (
                                    <span style={{ color: 'red' }}>*</span>
                                  )}
                                </label>
                                <Calendar
                                  view="date"
                                  dateFormat="mm/dd/yy"
                                  value={DateConversionUtility.convertDBDateToLocal(
                                    currentNADWaiver?.SubmissionDate
                                  )}
                                  onChange={e => {
                                    const formattedDate =
                                      DateConversionUtility.convertLocalToDBDateString(
                                        e.value as Date
                                      );

                                    updateNADWaiver(concept.Strength, {
                                      SubmissionDate: formattedDate as unknown as Date,
                                    });
                                  }}
                                  showIcon
                                  disabled={!currentNADWaiver?.Submitted || !isIterationActive}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <div className={styles.dropdown}>
                                <label htmlFor="negotiation">STATUS</label>
                                <Dropdown
                                  options={[
                                    { label: 'Pending', value: 'Pending' },
                                    { label: 'Approved', value: 'Approved' },
                                  ]}
                                  value={currentNADWaiver?.Status || 'Pending'}
                                  onChange={e =>
                                    updateNADWaiver(concept.Strength, {
                                      Status: e.value,
                                    })
                                  }
                                  placeholder="Select Status"
                                  className={styles.selectDropdown}
                                  style={{
                                    width: '15rem',
                                    backgroundColor:
                                      currentNADWaiver?.Status === 'Approved'
                                        ? '#d4f8cc'
                                        : '#ffcccc',
                                    border:
                                      currentNADWaiver?.Status === 'Approved'
                                        ? '1px solid #006400'
                                        : '1px solid #a80000',
                                  }}
                                  pt={{
                                    input: {
                                      style: {
                                        color:
                                          currentNADWaiver?.Status === 'Approved'
                                            ? '1px solid #006400'
                                            : '1px solid #a80000',
                                      },
                                    },
                                    trigger: {
                                      style: {
                                        color:
                                          currentNADWaiver?.Status === 'Approved'
                                            ? '1px solid #006400'
                                            : '1px solid #a80000',
                                      },
                                    },
                                  }}
                                  disabled={!isIterationActive}
                                />
                              </div>
                              <div className={styles.dropdown}>
                                <label htmlFor="negotiationsDate">
                                  APPROVED DATE{' '}
                                  {currentNADWaiver?.Status === 'Approved' && (
                                    <span style={{ color: 'red' }}>*</span>
                                  )}
                                </label>
                                <Calendar
                                  view="date"
                                  dateFormat="mm/dd/yy"
                                  value={
                                    currentNADWaiver?.Status === 'Approved'
                                      ? DateConversionUtility.convertDBDateToLocal(
                                          currentNADWaiver?.StatusDate
                                        )
                                      : null
                                  }
                                  onChange={e => {
                                    const formattedDate =
                                      DateConversionUtility.convertLocalToDBDateString(
                                        e.value as Date
                                      );

                                    updateNADWaiver(concept.Strength, {
                                      StatusDate: formattedDate as unknown as Date,
                                    });
                                  }}
                                  showIcon
                                  disabled={
                                    currentNADWaiver?.Status !== 'Approved' || !isIterationActive
                                  }
                                />
                              </div>
                            </div>
                            <div className={styles.dropdown}>
                              <label htmlFor="negotiationsDate">
                                EXPIRATION DATE{' '}
                                {currentNADWaiver?.Status === 'Approved' && (
                                  <span style={{ color: 'red' }}>*</span>
                                )}
                              </label>
                              <Calendar
                                view="date"
                                dateFormat="mm/dd/yy"
                                value={DateConversionUtility.convertDBDateToLocal(
                                  currentNADWaiver?.ExpirationDate
                                )}
                                onChange={e => {
                                  const formattedDate =
                                    DateConversionUtility.convertLocalToDBDateString(
                                      e.value as Date
                                    );

                                  updateNADWaiver(concept.Strength, {
                                    ExpirationDate: formattedDate as unknown as Date,
                                  });
                                }}
                                showIcon
                                disabled={
                                  !isIterationActive ||
                                  currentNADWaiver?.Status.toLowerCase() === 'pending'
                                }
                              />
                            </div>
                            {saveError && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  flexDirection: 'column',
                                }}
                              >
                                {saveError.map((item, errIdx) => (
                                  <span
                                    key={errIdx}
                                    style={{ color: 'red', fontSize: '14px', fontWeight: '600' }}
                                  >
                                    **{item.error}
                                    <br />
                                  </span>
                                ))}
                              </div>
                            )}
                            <Button
                              style={{ width: '6.5rem', alignSelf: 'flex-end' }}
                              label={savingStrength === concept.Strength ? 'Saving...' : 'Save'}
                              icon={
                                savingStrength === concept.Strength
                                  ? 'pi pi-spin pi-spinner'
                                  : 'pi pi-save'
                              }
                              onClick={() => saveStrength(concept.Strength)}
                              disabled={
                                savingStrength === concept.Strength ||
                                !isIterationActive ||
                                !checkIfFunctionalityEnabled(
                                  bdTrackerAllowedRoles.nadWaiverSaveButton,
                                  userModulesRoles.opptracker,
                                  userModulesRoles
                                )
                              }
                              className="p-button-primary"
                            />
                          </div>
                        </div>
                      )}
                      <div style={{ paddingBottom: '2rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <ReactQuill
                            value={note || ''}
                            onChange={content => handleNoteChange(content)}
                            placeholder="Add NAD notes"
                            style={{
                              height: '5rem',
                              width: '100%',
                              pointerEvents: !isIterationActive ? 'none' : 'auto',
                            }}
                            modules={{
                              toolbar: [
                                ['bold', 'italic', 'underline'],
                                ['link'],
                                [{ list: 'ordered' }, { list: 'bullet' }],
                                ['clean'],
                              ],
                            }}
                          />
                          <Button
                            label="Add"
                            style={{
                              backgroundColor: '#522D6E',
                              border: '1px solid #522D6E',
                              marginTop: '3rem',
                              alignSelf: 'flex-end',
                              padding: '0.5rem 0.75rem',
                            }}
                            disabled={
                              note === '' ||
                              !isIterationActive ||
                              !checkIfFunctionalityEnabled(
                                bdTrackerAllowedRoles.nadWaiverSaveButton,
                                userModulesRoles.opptracker,
                                userModulesRoles
                              )
                            }
                            loading={NADWaiverNoteLoading}
                            onClick={() => {
                              const payload: NADWaiverNoteRequest = {
                                OpportunityIterationID: currentNADWaiver?.OpportunityIterationID ?? 0,
                                Notes: note,
                                Strength: concept.Strength,
                              };
                              saveNADWaiverNote(payload);
                              setNote('');
                            }}
                          />
                        </div>
                        {NADWaiverNotes?.map((note, index) => (
                          <div
                            key={index}
                            style={{ paddingTop: '1rem', borderBottom: '1px solid #00000030' }}
                          >
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                              <h3 style={{ margin: 0 }}>{note.LastModifiedBy}</h3>
                            </div>
                            <div
                              style={{ margin: '1rem 0 1rem 1rem' }}
                              dangerouslySetInnerHTML={{ __html: note.Notes }}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  </TabPanel>
                );
              })}
            </TabView>
          </div>
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              width: '100%',
            }}
          >
            No concept available.
          </div>
        )}
        {isConfirmModalOpen && (
          <ConfirmationModal
            visibility={true}
            modalHeading="Confirmation"
            modalContent="Edits done will be lost, are you sure you want to continue ?"
            handleCancel={handleCloseConfirmationModal}
            handleConfirm={handleConfirmForConfirmationModal}
          />
        )}
      </>
    </Dialog>
  );
};

export default observer(NADWaiverModal);
